import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useVirtualizer } from '@tanstack/react-virtual';
import { spacerCellClassName } from './tableClasses';
import { observeScrollElementRect } from './virtualScroll';

const OVERSCAN = 10;
const NO_ROWS = Object.freeze([]);

function SpacerRow({ height, columnSpan }) {
    if (height <= 0) return null;
    return (
        <tr aria-hidden="true">
            <td colSpan={columnSpan} className={spacerCellClassName} style={{ height }} />
        </tr>
    );
}

SpacerRow.propTypes = {
    height: PropTypes.number.isRequired,
    columnSpan: PropTypes.number.isRequired,
};

/**
 * DataTable's frame (the scroll element) and <table>, holding the row
 * virtualizer. It renders the scroller itself so TanStack Virtual finds it
 * when it attaches. The header, colgroup and summary groups arrive as
 * elements, so the re-render a scroll causes here skips them.
 *
 * Without `virtual`, `children` are the body rows. With `virtual`
 * ({ rows, renderRow(row, index, rowRef), rowHeight, scrollMargin,
 * initialHeight, columnSpan, onRowMount, virtualizerRef }), the body is the
 * window of rows between two spacer rows (the TanStack Virtual table
 * pattern), so native table layout and the sticky header keep working.
 * Rows are measured as they mount (falling back to `rowHeight` without
 * layout), so compact mode's shorter rows scroll true.
 */
export default function DataTableFrame({
    frameRef,
    className,
    style,
    tableProps,
    colgroup,
    header,
    topSummary,
    bottomSummary,
    bodyProps,
    virtual,
    children,
}) {
    const rows = virtual?.rows ?? NO_ROWS;
    const rowHeight = virtual?.rowHeight ?? 0;
    const scrollMargin = virtual?.scrollMargin ?? 0;
    const initialHeight = virtual?.initialHeight ?? 0;
    const onRowMount = virtual?.onRowMount;
    const estimateSize = useCallback(() => rowHeight, [rowHeight]);
    const measureElement = useCallback(
        (element) => Math.round(element.getBoundingClientRect().height) || rowHeight,
        [rowHeight],
    );
    const getItemKey = useCallback((index) => rows[index]?.id ?? index, [rows]);
    const initialRect = useMemo(() => ({ width: 0, height: initialHeight }), [initialHeight]);
    const virtualizer = useVirtualizer({
        enabled: Boolean(virtual),
        count: rows.length,
        getScrollElement: () => frameRef.current,
        estimateSize,
        measureElement,
        getItemKey,
        overscan: OVERSCAN,
        scrollMargin,
        initialRect,
        observeElementRect: observeScrollElementRect,
    });
    // Stable, so React calls it only when a row mounts or unmounts.
    const rowRef = useCallback((node) => {
        virtualizer.measureElement(node);
        if (node) onRowMount?.(node);
    }, [virtualizer, onRowMount]);

    let bodyRows = children;
    if (virtual) {
        // DataTable's scrollToRow reads the measurements through it.
        virtual.virtualizerRef.current = virtualizer;
        const items = virtualizer.getVirtualItems();
        const paddingTop = items.length ? items[0].start - scrollMargin : 0;
        const paddingBottom = items.length
            ? virtualizer.getTotalSize() - (items[items.length - 1].end - scrollMargin)
            : 0;
        bodyRows = (
            <>
                <SpacerRow height={paddingTop} columnSpan={virtual.columnSpan} />
                {items.map((item) => virtual.renderRow(rows[item.index], item.index, rowRef))}
                <SpacerRow height={paddingBottom} columnSpan={virtual.columnSpan} />
            </>
        );
    }

    return (
        <div ref={frameRef} className={className} style={style}>
            <table {...tableProps}>
                {colgroup}
                {header}
                {topSummary}
                <tbody {...bodyProps}>{bodyRows}</tbody>
                {bottomSummary}
            </table>
        </div>
    );
}

DataTableFrame.propTypes = {
    frameRef: PropTypes.object.isRequired,
    className: PropTypes.string,
    style: PropTypes.object,
    tableProps: PropTypes.object.isRequired,
    colgroup: PropTypes.node,
    header: PropTypes.node,
    topSummary: PropTypes.node,
    bottomSummary: PropTypes.node,
    bodyProps: PropTypes.object.isRequired,
    virtual: PropTypes.shape({
        rows: PropTypes.array.isRequired,
        renderRow: PropTypes.func.isRequired,
        rowHeight: PropTypes.number.isRequired,
        scrollMargin: PropTypes.number.isRequired,
        initialHeight: PropTypes.number.isRequired,
        columnSpan: PropTypes.number.isRequired,
        onRowMount: PropTypes.func.isRequired,
        virtualizerRef: PropTypes.object.isRequired,
    }),
    children: PropTypes.node,
};
